import { useCallback, useEffect, useRef, useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import { useLocalStorage } from 'react-use';

import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { analytics } from '../../../services/analytics';
import { postRequest } from '../../../services/api';
import { bingoQueryKeys, queryClient } from '../../../services/queryClient';
import { useStores } from '../../../stores/useStores';

import { BINGO_FTUE_STORAGE_KEY } from './helper';
import type { BaseBingoRiveEventProperties, BingoFtueProgress, BingoRiveEventProperties } from './types';

export function useBingoFTUE() {
  const { bingoStore, userStore } = useStores();

  const isBingoFtueActive = bingoStore.shouldShowBingoFtue;

  const [showFtue, setShowFtue] = useState(false);
  const [savedStep, setSavedStep, clearSavedStep] = useLocalStorage<BingoFtueProgress | null>(
    BINGO_FTUE_STORAGE_KEY(userStore.profile?.firebaseUid || ''),
    null,
  );

  const hasFtueCompleteBeenCalledRef = useRef(false);
  const sentAnalyticsEventsRef = useRef<Set<string>>(new Set());
  const hasFtueStartedBeenSentRef = useRef(false);

  useHiddenBodyOverflow(showFtue);

  const sendPropertiesAnalytics = useCallback(
    (
      properties: BaseBingoRiveEventProperties,
      fileUrl: string,
      skipStepNumber: number,
      interactionType: 'click' | 'impression' = 'click',
    ) => {
      const eventName = properties.event_name as unknown as keyof AnalyticsEvents;

      if (!eventName || typeof eventName !== 'string') {
        return;
      }

      if (sentAnalyticsEventsRef.current.has(eventName)) {
        return;
      }
      sentAnalyticsEventsRef.current.add(eventName);

      void analytics.sendEvent(eventName, {
        event_category: 'bingo_tutorial',
        event_action: interactionType,
        step_name: properties.step_name || '',
        step_number: properties.stageNum ?? 0,
        skip_step_number: skipStepNumber || 0,
        url: fileUrl || '',
      });
    },
    [],
  );

  const closeFTUE = useCallback(() => {
    bingoStore.completeBingoFtue();
    setShowFtue(false);
    clearSavedStep();
  }, [bingoStore, clearSavedStep]);

  const finishBingoFtue = useCallback(() => {
    if (hasFtueCompleteBeenCalledRef.current) {
      return;
    }
    hasFtueCompleteBeenCalledRef.current = true;
    userStore.markTutorialAsFinished('BINGO');

    const requestBody: { type: string; extraParams?: { isNewTutorial: boolean } } = {
      type: 'BINGO',
    };

    if (!bingoStore.disableGiftCards) {
      requestBody.extraParams = {
        isNewTutorial: true,
      };
    }

    void postRequest('/user/finish-tutorial', requestBody);

    void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.currentEvent() });
    void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.upcomingEvents() });

    closeFTUE();
    bingoStore.openBingoSection();
  }, [closeFTUE, userStore, bingoStore]);

  const skipBingoFtue = useCallback(() => {
    void analytics.sendEvent('bingo_ftue_skipped', {
      event_category: 'bingo_tutorial',
      event_action: 'click',
      step_name: savedStep?.stepName || '',
      stepNum: savedStep?.stageNum || 0,
    });
    finishBingoFtue();
  }, [finishBingoFtue, savedStep]);

  const terminateBingoFtueFlow = useCallback(
    (reason: string) => {
      void analytics.sendEvent('bingo_ftue_terminated', {
        reason,
        event_category: 'bingo_tutorial',
        event_action: 'impression',
      });
      finishBingoFtue();
    },
    [finishBingoFtue],
  );

  const saveStepToLocalStorage = useCallback(
    (properties: BaseBingoRiveEventProperties, fileUrl: string) => {
      if (!('stageNum' in properties)) {
        return;
      }
      const currentStageNum = properties.stageNum || 0;
      if (savedStep && savedStep.stageNum >= currentStageNum) {
        return;
      }
      setSavedStep({
        stepName: properties.step_name,
        riveFileName: fileUrl,
        firebaseUserId: userStore.profile?.firebaseUid || '',
        stageNum: currentStageNum,
        eventName: properties.event_name,
      });
    },
    [savedStep, userStore.profile?.firebaseUid, setSavedStep],
  );

  const onBingoFtueEvent = useCallback(
    (properties: BingoRiveEventProperties, fileUrl: string, interactionType: 'click' | 'impression' = 'click') => {
      try {
        if ('isFinish' in properties && properties.isFinish) {
          finishBingoFtue();
          return;
        }

        if ('isSkip' in properties && properties.isSkip) {
          void skipBingoFtue();
          return;
        }

        if ('isLastStep' in properties && properties.isLastStep) {
          void analytics.sendEvent('bingo_ftue_completed', {
            event_category: 'bingo_tutorial',
            event_action: 'impression',
          });
          finishBingoFtue();
          return;
        }

        if ('event_name' in properties && 'step_name' in properties) {
          saveStepToLocalStorage(properties, fileUrl);
          sendPropertiesAnalytics(properties, fileUrl, 0, interactionType);
        }
      } catch (error) {
        terminateBingoFtueFlow(`error_handling_event: ${(error as Error)?.message}`);
      }
    },
    [finishBingoFtue, sendPropertiesAnalytics, terminateBingoFtueFlow, skipBingoFtue, saveStepToLocalStorage],
  );

  useEffect(() => {
    if (isBingoFtueActive && !hasFtueStartedBeenSentRef.current) {
      hasFtueStartedBeenSentRef.current = true;
      void analytics.sendEvent('bingo_ftue_started', {
        event_category: 'bingo_tutorial',
        event_action: 'impression',
      });
      setShowFtue(true);
    }
  }, [isBingoFtueActive]);

  return {
    showFtue: showFtue && isBingoFtueActive,
    terminateBingoFtueFlow,
    onBingoFtueEvent,
    savedStep,
  };
}
